import * as React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormSwitch, FormTextArea, FormDropDownList, FormAddress } from "../components/formComponents";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { GetPermissions } from '../components/Options';
import { getMultiSelectTreeValue } from "@progress/kendo-react-dropdowns";
import { processMultiSelectTreeData, expandedState  } from "../components/multiselecttree-data-operations";
import { MultiSelectTreeEditor } from "../components/MultiSelectTreeEditor";
import { Map, MapLayers, MapTileLayer, MapMarkerLayer } from '../components/Session';// from '@progress/kendo-react-map';
import { Dialog} from "@progress/kendo-react-dialogs";

const EditSources = () => {
    const { id } = useParams(); // editsources/id url is used to edit existing source, otherwise id is undefined
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [states, setStates] = React.useState([ ]);
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = React.useState(0);
    const handleTabSelect = (e) => {setSelectedTab(e.selected); };
    const [permissions, setPermissions] = React.useState(null);
    const [centre, setCentre] = React.useState([-33.8, 151]);
    const tileUrl = e => `https://${e.subdomain}.tile.openstreetmap.org/${e.zoom}/${e.x}/${e.y}.png`;
    const [markers, setMarkers] = React.useState([]);
    const [zoom, setZoom] = React.useState(12);
    const [showMap, setShowMap] = React.useState(false);
    if (localStorage.getItem("userid") === null) navigate("/logout");

    // default layout data for page
    const [layout, setLayout] = React.useState([
        { "field": "sourceDetails", "label": "Source", "defaultValue": "", "isVisible": true, "type": "heading"},
        { "field": "name", "label": "Name", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "code", "label": "Code", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "description", "label": "Comment", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "owfSource", "label": "OWF Source", "defaultValue": "NA", "isVisible": true, "type": "text"},
        { "field": "owfEplNumber", "label": "OWF EPL Number", "defaultValue": "NA", "isVisible": false, "type": "boolean"},
        { "field": "address1", "label": "Address Line 1", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "address2", "label": "Address Line 2", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "suburb", "label": "Suburb", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "state", "label": "State", "defaultValue": "NSW", "isVisible": true, "type": "text"},
        { "field": "postcode", "label": "Postcode", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "country", "label": "Country", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "phone", "label": "Phone", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "mobile", "label": "Mobile", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "contact", "label": "Contact", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "description", "label": "Comment", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "email", "label": "Email", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "active", "label": "Active", "defaultValue": "TRUE", "isVisible": true, "type": "boolean"},
        { "field": "siteDetails", "label": "Site details", "defaultValue": "", "isVisible": true, "type": "heading"},
        { "field": "addressDetails", "label": "Address and contact details", "defaultValue": "", "isVisible": true, "type": "heading" },
        { "field": "isStockpile", "label": "Stockpile", "defaultValue": "False", "isVisible": true, "type": "boolean" }
    ]);

    // Gets field from layout based on field name
    const GetField = (f, def = "") => {
        let fld = layout.find(obj => { return obj.field === f });
        if (fld === undefined) return { "field": f, "label": def, "defaultValue": "", "isVisible": true, "type": "text" }
        return fld;
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        name: GetField("name")["defaultValue"],
        code: GetField("code")["defaultValue"],
        description: GetField("description")["defaultValue"],
        owfSource: GetField("owfSource")["defaultValue"],
        owfEplNumber: GetField("owfEplNumber")["defaultValue"],
        address1: GetField("address1")["defaultValue"],
        address2: GetField("address2")["defaultValue"],
        suburb: GetField("suburb")["defaultValue"],
        state: GetField("state")["defaultValue"],
        postcode: GetField("postcode")["defaultValue"],
        country: GetField("country")["defaultValue"],
        phone: GetField("phone")["defaultValue"],
        mobile: GetField("mobile")["defaultValue"],
        contact: GetField("contact")["defaultValue"],
        email: GetField("email")["defaultValue"],
        siteDetails: GetField("siteDetails")["defaultValue"],
        addressDetails: GetField("addressDetails")["defaultValue"],
        active: String(GetField("active")["defaultValue"]).toLowerCase() === "true",
        isStockpile: String(GetField("isStockpile")["defaultValue"]).toLowerCase() === "true"
      });

    // When page first loads:
    // - populate drop downs from apiv7/options
    // - if in edit mode, set initial values for all fields for apiv7/vehicleconfigurations
    React.useEffect(() => {
        GetPermissions(["ViewSource", "NewSource", "EditSource"], setPermissions);
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'EditSources'";
        const init = {
          method: 'GET',
          accept: 'application/json',
          headers: {"Authorization": "Bearer " + GetToken()}
        }
        fetch(url, init).then(response => response.json()).then(d => { 
            setLayout(d); });
        url = configData.SERVER_URL + "apiv7/options";
        fetch(url, init).then(response => response.json()).then(opt => {           
            setStates(opt.states);
            let allSites = GetList(opt.sites);
            let defSites = ConfigureSites(opt.sites, opt.inactiveSites, allSites);
            if (id !== undefined){
                url = `${configData.SERVER_URL}apiv7/sources/${id}`;
                fetch(url, init).then(response => response.json()).then(d => {
                    let sp = d.siteList === null ? [] : String(d.siteList).split(',');
                    let sites = ConfigureSites(opt.sites, opt.inactiveSites, d.siteList);
                    setValues({
                        name: d.name === null ? "" : d.name,
                        code: d.code === null ? "" : d.code,
                        description: d.description === null ? "" : d.description,
                        owfSource: d.owfSource === null ? "" : d.owfSource,
                        owfEplNumber: d.owfEplNumber === null ? "" : d.owfEplNumber,
                        address1: d.address1 === null ? "" : d.address1,
                        address2: d.address2 === null ? "" : d.address2,
                        suburb: d.suburb === null ? "" : d.suburb,
                        state: d.state === null ? "" : d.state,
                        postcode: d.postcode === null ? "" : d.postcode,
                        country: d.country === null ? "" : d.country,
                        phone: d.phone === null ? "" : d.phone,
                        mobile: d.mobile === null ? "" : d.mobile,
                        contact: d.contact === null ? "" : d.contact,
                        email: d.email === null ? "" : d.email,
                        siteDetails: d.siteDetails === null ? "" : d.siteDetails,
                        addressDetails: d.addressDetails === null ? "" : d.addressDetails,
                        sourceSites: sites,
                        active: d.isActive,
                        isStockpile: d.isStockpile
                    });
                    setFormkey(formkey + 1);
                })
            } else {
                var v = {...values};
                v.sourceSites = defSites;
                setValues(v);                
                setFormkey(formkey + 1);
            }
        });
    }, []);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (fld === undefined || fld === null) return {};
        if (fld["isVisible"]) return f;
        let res = "";
        switch(fld["type"]){
            case "text": res = fld["defaultValue"]; break;
            case "numeric": res = parseFloat(fld["defaultValue"]); break;
            case "boolean": res = fld["defaultValue"] === "true"; break;
            default: res = fld["defaultValue"]; break;
        }
        return res;
    }

    const [sitesData, setSitesData] = React.useState([{id:0, text: "Active", items: []}]);
    const dataItemKey = "id";
    const checkField = "checkField";
    const checkIndeterminateField = "checkIndeterminateField";
    const subItemsField = "items";
    const expandField = "expanded";
    const fields = { dataItemKey, checkField, checkIndeterminateField, expandField, subItemsField };

    const [sitesValue, setSitesValue] = React.useState([]);
    const [siteExpanded, setSiteExpanded] = React.useState([]);
    const sitesTreeData = React.useMemo(() => processMultiSelectTreeData(sitesData, { expanded: siteExpanded, value: sitesValue, ...fields }), [siteExpanded, sitesValue]);
    const ConfigureSites = (act, dis, siteList) => {
        if (siteList === null) return [];
        let slsp = siteList.split(',');
        let sl = [];
        let selected = [];
        for (let i = 0; i < act.length; i++){
            let entry = { text: act[i].name, id: act[i].id };
            sl.push(entry);
            let found = slsp.find(obj => { return obj === String(act[i].id) });
            if (found !== undefined) selected.push(entry);
        }
        let active = {id: 0, text: 'Active', items: sl };
        if (dis !== undefined && dis.length > 0){
            sl = [];
            for (let i = 0; i < dis.length; i++){
                let entry = { text: dis[i].name, id: dis[i].id };
                sl.push(entry);       
                let found = slsp.find(obj => { return obj === String(dis[i].id) });
                if (found !== undefined) selected.push(entry);
            }
            let inactive = {id: 999999, text: 'Inactive', items: sl };
            setSitesData([active, inactive]);
        }
        else {
            setSitesData([active]);
        }
        setSitesValue([...selected]);       
        return [...selected];
    }

    const GetList = (obj) => {
        if (obj === undefined || obj.length === 0) return "";
        var l = "";
        for (let i = 0; i < obj.length; i++){
            let id = obj[i].id;
            if (id !== 0 && id !== 999990){
                l += id;
                if (i < obj.length - 1) l += ",";
            }
        }
        return l;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        let dataItem = { ...values };
        var pth = id === undefined ? "" : "/" + id;
        var siteList = GetList(dataItem.sourceSites);
        var url = `${configData.SERVER_URL}apiv7/sources${pth}`;
        const init = {
          method: id === undefined ? 'POST' : 'PUT',
          accept: "application/json",
          headers: {
            "Content-type": "application/json",
            "Authorization": "Bearer " + GetToken()
          },
          mode: "cors",
          cache: "no-cache",
          body: JSON.stringify({
            "sourceId": id === undefined ? 0 : id,
            "name": GetDefaultData(dataItem.name, "name"),
            "code": GetDefaultData(dataItem.code, "code"),
            "description": GetDefaultData(dataItem.description, "description"),
            "owfSource": GetDefaultData(dataItem.owfSource, "owfSource"),
            "owfEplNumber": GetDefaultData(dataItem.owfEplNumber, "owfSourowfEplNumberce"),
            "address1": GetDefaultData(dataItem.address1, "address1"),
            "address2": GetDefaultData(dataItem.address2, "address2"),
            "suburb": GetDefaultData(dataItem.suburb, "suburb"),
            "state": GetDefaultData(dataItem.state, "state"),
            "postcode": GetDefaultData(dataItem.postcode, "postcode"),
            "country": GetDefaultData(dataItem.country, "country"),
            "phone": GetDefaultData(dataItem.phone, "phone"),
            "mobile": GetDefaultData(dataItem.mobile, "mobile"),
            "contact": GetDefaultData(dataItem.contact, "contact"),
            "comment": GetDefaultData(dataItem.comment, "comment"),
            "email": GetDefaultData(dataItem.email, "email"),
            "siteList": siteList,
            "isActive": GetDefaultData(dataItem.active, "active"),
            "isStockpile": GetDefaultData(dataItem.isStockpile, "isStockpile") 
           })
        }
        fetch(url, init).then(response => response).then(d => { 
            var msg = id === undefined ? "Source created" : "Source updated";
            Redirect(msg);
        });
    }

    const AddressChanged = (e) => {
        if (e !== null && e.value !== undefined && e.value.terms !== undefined && e.value.terms.length > 2) {
            let v = { ...values };
            v.address1 = e.value.structured_formatting.main_text;
            v.address2 = "";
            var ind = 1;
            if (e.value.terms.length > 3) ind = e.value.terms.length - 2;
            v.suburb = e.value.terms[ind++].value;
            v.state = e.value.terms[ind].value;
            setValues(v);
            setFormkey(new Date());
        }
    }

    const DisplayMap = (e) => {
        let addr = values.address1;
        if (!values.address2 !== "") addr += "+" + values.address2;
        if (!values.suburb !== "") addr += "+" + values.suburb;
        if (!values.state !== "") addr += "+" + values.state;
        if (!values.postcode !== "") addr += "+" + values.postcode;
        addr += "+AU";
        let a = encodeURIComponent(addr);
        var url = `${configData.SERVER_URL}apiv7/private/getlocation?address=${a}`;
        const init = {
            method: 'GET',
            accept: "application/json",
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            mode: "cors",
            cache: "no-cache"
        }
        fetch(url, init).then(response => response.json()).then(l => {
            let m = [];
            setCentre([l.latitude, l.longitude]);
            m.push({ position: [l.latitude, l.longitude], name: values.address1 });
            setMarkers(m);
            setShowMap(true);
            setFormkey(formkey + 1);
        });
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
       navigate("/sources", {state: {msg: msg}});
    }
    if (permissions && !permissions.CanView) navigate("/error401");
    if (permissions && !permissions.CanEdit && id !== undefined) navigate("/error401");
    if (permissions && !permissions.CanCreate && id === undefined) navigate("/error401");

    const NameChanged = (e) => { let v = { ...values }; v.name = e.value; setValues(v); };
    const CodeChanged = (e) => { let v = { ...values }; v.code = e.value; setValues(v); };
    const DescriptionChanged = (e) => { let v = { ...values }; v.description = e.value; setValues(v); };
    const ActiveChanged = (e) => { let v = { ...values }; v.active = e.value; setValues(v); };
    const OwfSourceChanged = (e) => { let v = { ...values }; v.owfSource = e.value; setValues(v); };
    const OwfEplNumberChanged = (e) => { let v = { ...values }; v.owfEplNumber = e.value; setValues(v); };
    const IsStockpileChanged = (e) => { let v = { ...values }; v.isStockpile = e.value; setValues(v); };

    const Address2Changed = (e) => { let v = { ...values }; v.address2 = e.value; setValues(v); };
    const SuburbChanged = (e) => { let v = { ...values }; v.suburb = e.value; setValues(v); };
    const StateChanged = (e) => { let v = { ...values }; v.state = e.value; setValues(v); };
    const PostcodeChanged = (e) => { let v = { ...values }; v.postcode = e.value; setValues(v); };
    const CountryChanged = (e) => { let v = { ...values }; v.country = e.value; setValues(v); };
    const PhoneChanged = (e) => { let v = { ...values }; v.phone = e.value; setValues(v); };
    const ContactChanged = (e) => { let v = { ...values }; v.contact = e.value; setValues(v); };
    const MobileChanged = (e) => { let v = { ...values }; v.mobile = e.value; setValues(v); };
    const EmailChanged = (e) => { let v = { ...values }; v.email = e.value; setValues(v); };
    const SitesChanged = (e) => { let v = { ...values }; v.sourceSites = e.value; setValues(v); };

    // form validators
    const textValidator = (value) =>  !value ? "Please enter a value" : "";
    return (
        <div style={{padding: 20}}>
            <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                <FormElement style={{width: "95%"}} >
                    <fieldset className={"k-form-fieldset"}>
                    <TabStrip selected={selectedTab} onSelect={handleTabSelect}>
                        {GetField("sourceDetails")["isVisible"] && <TabStripTab title={GetField("sourceDetails")["label"]}>
                        <div className="row">
                           {GetField("name")["isVisible"] && <div className="col-md-3">
                                <Field id={"name"} name={"name"} label={GetField("name")["label"]} component={FormInput} validator={textValidator} onChange={NameChanged} />
                            </div>}
                            {GetField("code")["isVisible"] && <div className="col-md-3">
                                <Field id={"code"} name={"code"} label={GetField("code")["label"]} component={FormInput} validator={textValidator} onChange={CodeChanged} />
                            </div>}
                            {GetField("description")["isVisible"] && <div className='col-md-3'>
                                <Field id={"description"} name={"description"} label={GetField("description")["label"]} component={FormTextArea} onChange={DescriptionChanged} />
                            </div>}                            
                            {GetField("active")["isVisible"] && <div className='col-md-3'>
                                <Field id={"active"} name={"active"} label={GetField("active")["label"]} component={FormSwitch} onChange={ActiveChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("owfSource")["isVisible"] && <div className="col-md-3">
                                <Field id={"owfSource"} name={"owfSource"} label={GetField("owfSource")["label"]} component={FormInput} onChange={OwfSourceChanged} />
                            </div>}
                            {GetField("owfEplNumber")["isVisible"] && <div className="col-md-3">
                                <Field id={"owfEplNumber"} name={"owfEplNumber"} label={GetField("owfEplNumber")["label"]} component={FormInput} onChange={OwfEplNumberChanged} />
                            </div>}
                            {GetField("isStockpile")["isVisible"] && <div className='col-md-3'>
                                <Field id={"isStockpile"} name={"isStockpile"} label={GetField("isStockpile")["label"]} component={FormSwitch} onChange={IsStockpileChanged} />
                            </div>}
                        </div>
                        </TabStripTab>}
                        {GetField("addressDetails")["isVisible"] && <TabStripTab title={GetField("addressDetails")["label"]}>
                        <div className='row' >
                            {GetField("address1")["isVisible"] && <div className='col-md-3'>
                                <Field id={"address1"} name={"address1"} label={GetField("address1")["label"]} component={FormAddress} onChange={AddressChanged}/>
                            </div>}
                            {GetField("address2")["isVisible"] && <div className='col-md-3'>
                                <Field id={"address2"} name={"address2"} label={GetField("address2")["label"]} component={FormInput} onChange={Address2Changed}  />
                            </div>}
                            {GetField("suburb")["isVisible"] && <div className='col-md-3'>
                                <Field id={"suburb"} name={"suburb"} label={GetField("suburb")["label"]} component={FormInput} onChange={SuburbChanged} />
                            </div>}
                            {GetField("state")["isVisible"] && <div className='col-md-3'>
                                <Field id={"state"} name={"state"} label={GetField("state")["label"]} component={FormDropDownList} data={states} onChange={StateChanged} />
                            </div>}                           
                        </div>
                        <div className='row' >
                            {GetField("postcode")["isVisible"] && <div className='col-md-3'>
                                <Field id={"postcode"} name={"postcode"} label={GetField("postcode")["label"]} component={FormInput} onChange={PostcodeChanged} />
                            </div>}
                            {GetField("country")["isVisible"] && <div className='col-md-3'>
                                <Field id={"country"} name={"country"} label={GetField("country")["label"]} component={FormInput} onChange={CountryChanged} />
                            </div>}
                            {GetField("phone")["isVisible"] && <div className='col-md-3'>
                                <Field id={"phone"} name={"phone"} label={GetField("phone")["label"]} component={FormInput} onChange={PhoneChanged}  />
                            </div>}
                            {GetField("contact")["isVisible"] && <div className='col-md-3'>
                                <Field id={"contact"} name={"contact"} label={GetField("contact")["label"]} component={FormInput} onChange={ContactChanged} />
                            </div>}
                        </div>
                        <div className='row' >
                            {GetField("mobile")["isVisible"] && <div className='col-md-3'>
                                <Field id={"mobile"} name={"mobile"} label={GetField("mobile")["label"]} component={FormInput} onChange={MobileChanged} />
                            </div>}
                            {GetField("email")["isVisible"] && <div className='col-md-3'>
                                <Field id={"email"} name={"email"} label={GetField("email")["label"]} component={FormInput} onChange={EmailChanged} />
                            </div>}
                            {false && GetField("showMap")["isVisible"] && <div className='col-md-3' style={{ paddingTop: "30px" }}>
                                <Button onClick={DisplayMap} >{GetField("showMap", "Show Map")["label"]}</Button>
                            </div>}
                        </div>
                        </TabStripTab>}
                            {GetSession().SiteID === 1 && GetField("siteDetails")["isVisible"] && <TabStripTab title={GetField("siteDetails")["label"]}>
                            <div className="row">
                                {GetField("siteDetails")["isVisible"] && <div className="col-md-6"><h5>{GetField("siteDetails")["label"]}</h5></div>}
                            </div>                                        
                            <div className='row' >
                                {GetField("siteDetails")["isVisible"] && <div className='col-md-6'>
                                    <Field component={MultiSelectTreeEditor} name={"sourceSites"} data={sitesTreeData} dataItemKey={dataItemKey} checkField={checkField} size="large" onChange={SitesChanged} />                                
                                </div>}
                            </div></TabStripTab>}
                        </TabStrip>    
                        </fieldset>
                    <div className="k-form-buttons" style={{paddingTop: 10}}>
                        <div className='col-md-1'>
                            <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit}>{saveName}</Button>
                        </div>
                        <div className='col-md-1'>
                            <Button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={GoBack}>Cancel</Button>
                        </div>
                    </div>                
                </FormElement>
            )} />
            {showMap && <Dialog title="Address Map" onClose={() => setShowMap(false)}>
                <div style={{ width: "1000px" }}>
                <Map center={centre} zoom={zoom} minZoom={5}>
                    <MapLayers>
                        <MapTileLayer urlTemplate={tileUrl} />
                        <MapMarkerLayer data={markers} locationField="position" titleField="name" />
                    </MapLayers>
                </Map>
                </div>
            </Dialog>}
        </div>
    );
}

export default EditSources